import { useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useUserExperiences } from "@/hooks/useUserExperiences";
import { StarRating } from "./StarRating";

interface InteractiveRatingProps {
  tipId: string;
  currentRating: number;
  ratingCount: number;
  title: string;
  className?: string;
}

export const InteractiveRating = ({ 
  tipId, 
  currentRating, 
  ratingCount, 
  title,
  className = "" 
}: InteractiveRatingProps) => {
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [experienceText, setExperienceText] = useState("");
  const [showForm, setShowForm] = useState(false);
  const { submitExperience, isLoading } = useUserExperiences();

  const handleStarClick = (rating: number) => {
    setUserRating(rating);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (userRating > 0) {
      const result = await submitExperience(tipId, experienceText, userRating);
      if (result.success) {
        setShowForm(false);
        setUserRating(0);
        setExperienceText("");
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setUserRating(0);
    setExperienceText("");
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Current Rating Display */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <StarRating rating={currentRating} showNumber={true} />
          <span className="text-sm text-muted-foreground">
            ({ratingCount} {ratingCount === 1 ? 'rating' : 'ratings'})
          </span>
        </div>
      </div>

      {/* Interactive Rating */}
      {!showForm && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Rate this tip:</p>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => handleStarClick(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="transition-all duration-200 hover:scale-110"
                aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
              >
                <Star
                  className={`h-6 w-6 cursor-pointer transition-colors ${
                    star <= (hoverRating || userRating)
                      ? 'fill-primary text-primary'
                      : 'text-muted-foreground hover:text-primary'
                  }`}
                />
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Click a star to rate this tip
          </p>
        </div>
      )}

      {/* Rating Form */}
      {showForm && (
        <Card className="bg-card/50 backdrop-blur-sm border-border/50">
          <CardHeader>
            <CardTitle className="text-lg">Rate "{title}"</CardTitle>
            <CardDescription>
              You rated this tip {userRating} star{userRating !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`h-5 w-5 ${
                    star <= userRating
                      ? 'fill-primary text-primary'
                      : 'text-muted-foreground'
                  }`}
                />
              ))}
            </div>
            
            <div className="space-y-2">
              <label htmlFor="experience" className="text-sm font-medium">
                Share your experience (optional):
              </label>
              <Textarea
                id="experience"
                placeholder="How did this tip help you? What was your experience trying it?"
                value={experienceText}
                onChange={(e) => setExperienceText(e.target.value)}
                className="min-h-[100px] resize-none"
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handleSubmit}
                disabled={isLoading}
                className="flex-1"
              >
                {isLoading ? "Submitting..." : "Submit Rating"}
              </Button>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};